import React from "react";
import tw from "twin.macro";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { Headline } from "~/features/ui/headline";
import { IconButton } from "~/features/ui/icon-button";
import { stripHtmlTags } from "~/features/app/utils";
import Plus from "~/public/icons/plus.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";

type Props = {
  headline?: string;
  count?: string;
  text?: React.ReactNode;
  url?: string;
  linkText?: string;
  hasPlusIcon?: boolean;
  media?: React.ReactNode;
  tagsPosition?: "body" | "media-overlay";
  tags?: React.ReactNode;
  /** Metadata row rendered above the headline, alongside media-overlay tags. */
  meta?: React.ReactNode;
  /** Tints the media's plus control on card hover, as the media-grid cards do. */
  highlightIconOnHover?: boolean;
  footer?: React.ReactNode;
};

export function EditorialCard(props: Props) {
  const {
    headline,
    text,
    url,
    media,
    tagsPosition = "media-overlay",
    tags,
    meta,
    linkText,
    footer,
    hasPlusIcon = true,
    highlightIconOnHover,
    count,
  } = props;

  const content = (
    <div tw="flex flex-col flex-1 gap-24 lg:gap-40">
      <div tw="relative">
        {tags && tagsPosition === "media-overlay" && <div tw="absolute right-sm top-sm z-10">{tags}</div>}
        {!Boolean(linkText) && hasPlusIcon && (
          <div
            css={[
              tw`absolute z-10 bottom-sm right-sm`,
              // The button is a plain div here, so its own variant hover never fires.
              // Overriding from the card's `group` is what the media-grid cards do.
              highlightIconOnHover &&
                tw`[&>*]:transition-colors [&>*]:duration-250 can-hover:group-hover:[&>*]:!bg-blue-100`,
            ]}
          >
            <IconButton as="div" icon={{ component: Plus }} />
          </div>
        )}
        {media}
      </div>

      <div tw="flex flex-col flex-1">
        {tags && (tagsPosition === "body" || !media) && tags}
        {meta}
        {headline && (
          <div tw="flex mt-16 items-center gap-x-16 lg:gap-x-24">
            {Boolean(count) && (
              <span tw="text-mobile-caption-20-normal lg:text-desktop-caption-20-normal font-aeonik">{count}</span>
            )}
            <Headline
              type="h2"
              tw="text-mobile-subtitle-30-normal lg:text-desktop-subtitle-30-normal"
              text={headline}
            />
          </div>
        )}
        {text && (
          <div tw="text-mobile-body-20-normal lg:text-desktop-body-20-normal mt-16">
            {typeof text === "string" ? (
              <p tw="line-clamp-3">{stripHtmlTags(text)}</p>
            ) : (
              <div tw="line-clamp-3">{text}</div>
            )}
          </div>
        )}
        {url && Boolean(linkText) && (
          <div tw="mt-auto pt-40">
            <Button as="div" icon={ArrowRight} variant="underline">
              {linkText}
            </Button>
          </div>
        )}
        {footer && <div tw="mt-auto">{footer}</div>}
      </div>
    </div>
  );

  // `group` lets a card's media opt into a hover effect keyed on the whole card
  // rather than on the media alone (see `CaseStudyCard`). No descendant of this
  // component binds to it, so it is inert for cards that don't use it.
  if (url) {
    return (
      <Link tw="relative flex flex-col h-full" href={url} className="group">
        {content}
      </Link>
    );
  }

  return (
    <div tw="relative flex flex-col h-full" className="group">
      {content}
    </div>
  );
}
